import React from "react";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { ChartType } from "src/utils/types";
import PortfolioBalance from "./balance/PortfolioBalance";
import BalanceDefault from "./balance/BalanceDefault";
import {
  BALANCE_DATA_DEFAULT,
  IBalanceData,
  ITimePeriodOption,
  TIME_FILTER_OPTIONS,
  TIME_PERIOD_OPTIONS,
} from "./defaults";
import {
  formatBalance,
  formatChange,
  formatDollarChange,
  formatPrice,
  // isToday,
  removeCurvedBrackets,
} from "./helpers";
import useCompareButton from "./hooks/useCompareButton";
import useTimeFilter from "./hooks/useTimeFilter";
import { RangeSwitchOpts } from "../dashboard/chart/dashboardChart.types";
import useFormatBalanceData from "../dashboard/chart/useFormatBalanceChart";
import { styled } from "@mui/styles";

export const GreenPlus = styled("span")({
  color: "#056645",
  backgroundColor: "#F7FFFC",
});

export const RedMinus = styled("span")({
  color: "#790202",
  backgroundColor: "#FEF2F2",
});

const Balance = () => {
  const { overview, userHoldings, isUnverified } = useUserSlice();
  const { balance } = overview;
  const { data: userData } = api.useGetUserDatav2Query();
  const code = userData?.defaultCurrencyConfig.code;

  // get user wallet balance
  const [getWalletBalance, { data: walletBalance }] = api.useLazyGetWalletPageBalanceQuery({
    pollingInterval: 60000, // every 60 seconds
  });

  // call wallet balance here
  React.useEffect(() => {
    if (code) {
      getWalletBalance({ baseCurrency: code });
    }
  }, [code]);

  // on time filter click
  const { selectedTime, onTimeClick: handleOnTimeFilterClick } = useTimeFilter();

  // call api here
  // get chart data - portfolio running total, btc, eth.
  const { data: CBPortfolioData } = api.useGetAllUserRunningBalanceChartsQuery(undefined, {
    pollingInterval: 60000,
  });

  // =================================================
  //  Disabling until the compare component is ready
  // =================================================
  // const { data: BTCData } = api.useGetTokenChartDataQuery(
  //   {
  //     token: "BTC",
  //     interval: selectedTime.toUpperCase(),
  //     startDate,
  //     quote: code,
  //   },
  //   { skip: !startDate || !code },
  // );

  // const { data: ETHData } = api.useGetTokenChartDataQuery(
  //   {
  //     token: "ETH",
  //     interval: selectedTime.toUpperCase(),
  //     startDate,
  //     quote: code,
  //   },
  //   { skip: !startDate || !code },
  // );

  // get balance data - best performing holdings, total balance, change in balance.
  const { data: bestPerforming, isLoading } = api.useGetBestPerformingHoldingsQuery();

  // on compare click
  const {
    compareOptions,
    compareActive,
    onClick: handleOnCompareClick,
    onToggle: handleOnCompareOptionToggle,
    onClear: handleOnCompareClear,
  } = useCompareButton();

  const { data: formattedCB } = useFormatBalanceData(
    CBPortfolioData,
    selectedTime.toUpperCase() as RangeSwitchOpts,
  );
  const { data: formattedBTC } = useFormatBalanceData(
    undefined,
    // BTCData,
    selectedTime.toUpperCase() as RangeSwitchOpts,
  );
  const { data: formattedETH } = useFormatBalanceData(
    undefined,
    // ETHData,
    selectedTime.toUpperCase() as RangeSwitchOpts,
  );

  // format data from BE for balance component
  const balanceData = React.useMemo((): IBalanceData => {
    // Check if data exists, if not, use the default value
    if (!code || !bestPerforming || !walletBalance) return BALANCE_DATA_DEFAULT;
    const total = walletBalance.pending + walletBalance.balance.value;

    const formattedBalance = walletBalance
      ? formatBalance(total, code)
      : BALANCE_DATA_DEFAULT.balance;
    const formattedChange = walletBalance
      ? `${(walletBalance?.balance?.change ?? 0).toFixed(2)}%`
      : BALANCE_DATA_DEFAULT.change;
    const formattedDollarChange = balance
      ? formatDollarChange(walletBalance.balance.dollarChange ?? 0)
      : BALANCE_DATA_DEFAULT.dollarChange;
    const formattedBestPerforming = bestPerforming.map((asset) => {
      const bestPerformerPercentChange = formatChange(asset.change ?? 0);
      const isNegative = bestPerformerPercentChange[0] === "-";

      const assetChange = isNegative ? (
        <RedMinus data-testid="#bestperforming-negative-percent-change" translate="no">
          {bestPerformerPercentChange}
        </RedMinus>
      ) : (
        <GreenPlus data-testid="#bestperforming-positive-percent-change" translate="no">
          +{bestPerformerPercentChange}
        </GreenPlus>
      );
      return {
        name: removeCurvedBrackets(asset.currencyConfig.name),
        code: asset.currencyConfig.code ?? "USD",
        icon: (
          <img
            src={asset.currencyConfig.logoUrl}
            alt={asset.currencyConfig.name}
            width="24px"
            height="24px"
          />
        ),
        amount: formatPrice(asset.totalValue, code ?? "USD"),
        percent: assetChange,
      };
    });

    return {
      balance: formattedBalance,
      change: formattedChange,
      dollarChange: formattedDollarChange,
      unformattedValues: { change: walletBalance?.balance?.change ?? 0 },
      bestPerformers: formattedBestPerforming,
    };
  }, [overview, code, bestPerforming, walletBalance]);

  // get holdings data for donut chart
  const holdingsData = React.useMemo((): ChartType[] => {
    if (!userHoldings) return [];
    return userHoldings
      .filter((asset) => asset.currencyConfig.type === "Crypto")
      .map((asset) => ({
        value: asset.value,
        pending: asset.pendingValue,
        total: asset.total,
        balance: asset.available,
        name: removeCurvedBrackets(asset.currencyConfig.name),
        cryptoLogo: asset.currencyConfig.logoUrl ?? "",
        icon: (
          <img
            src={asset.currencyConfig.logoUrl}
            alt={asset.currencyConfig.name}
            width="24px"
            height="24px"
          />
        ),
      }));
  }, [userHoldings]);

  const [timePeriod, setTimePeriod] = React.useState<string>(TIME_PERIOD_OPTIONS[1].label);
  const handleTimePeriod = (newOpt: ITimePeriodOption): void => setTimePeriod(newOpt.label);

  const SHOW_DEFAULT = isLoading || userHoldings.length === 0;

  return (
    <>
      {isUnverified || SHOW_DEFAULT ? ( // IS UNVERIFIED or BLANK VERIFIED ACCOUNT
        <BalanceDefault
          isLoading={isLoading}
          isCompareActive={compareActive}
          selectedTime={selectedTime}
          bitcoinData={formattedBTC}
          ethereumData={formattedETH}
          holdingsData={holdingsData}
          compareOptions={compareOptions}
          timeFilterOptions={TIME_FILTER_OPTIONS}
          timePeriodOptions={TIME_PERIOD_OPTIONS}
          timePeriod={timePeriod}
          handleTimePeriod={handleTimePeriod}
          onCompareClick={handleOnCompareClick}
          onToggleCompareOptions={handleOnCompareOptionToggle}
          OnTimeFilterClick={handleOnTimeFilterClick}
          onCompareClear={handleOnCompareClear}
        />
      ) : (
        <PortfolioBalance
          balanceData={balanceData}
          chartData={formattedCB}
          bitcoinData={formattedBTC}
          ethereumData={formattedETH}
          holdingsData={holdingsData}
          isCompareActive={compareActive}
          compareOptions={compareOptions}
          selectedTime={selectedTime}
          timeFilterOptions={TIME_FILTER_OPTIONS}
          timePeriodOptions={TIME_PERIOD_OPTIONS}
          timePeriod={timePeriod}
          handleTimePeriod={handleTimePeriod}
          onCompareClick={handleOnCompareClick}
          onToggleCompareOptions={handleOnCompareOptionToggle}
          OnTimeFilterClick={handleOnTimeFilterClick}
          onCompareClear={handleOnCompareClear}
        />
      )}
    </>
  );
};

export default Balance;
